import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { FixtureItem } from '../components/views/FebView/types';
import { IMeshFactory } from './interfaces';

export class MeshFactory implements IMeshFactory {
  private gltfLoader: GLTFLoader;
  private modelCache: Record<string, THREE.Group> = {};

  constructor() {
    this.gltfLoader = new GLTFLoader();
  }

  /**
   * Create a text mesh with canvas-based texture
   */
  public createTextMesh(
    position: THREE.Vector3,
    text = 'Text'
  ): THREE.Mesh | null {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      console.error('Failed to get canvas context');
      return null;
    }

    canvas.width = 1024;
    canvas.height = 256;
    context.fillStyle = 'rgba(255, 255, 255, 1)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = 'black';
    context.font = 'bold 96px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Create material with the texture
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
    });

    const geometry = new THREE.PlaneGeometry(0.4, 0.1);
    const textMesh = new THREE.Mesh(geometry, material);

    // Position the text mesh
    textMesh.position.copy(position);
    textMesh.position.y = 0.01; // Slightly above floor
    textMesh.rotation.x = -Math.PI / 2; // Rotate to lay flat on the floor

    // Add user data for interaction
    textMesh.userData = {
      isInteractive: true,
      isText: true,
      text: text,
    };

    return textMesh;
  }

  /**
   * Update text mesh with new text content
   */
  public updateTextMesh(mesh: THREE.Mesh, newText: string): void {
    // Get the current material
    const material = mesh.material as THREE.MeshBasicMaterial;

    // Dispose of the old texture if it exists
    if (material.map) {
      material.map.dispose();
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = 1024;
    canvas.height = 256;
    context.fillStyle = 'rgba(255, 255, 255, 1)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = 'black';
    context.font = 'bold 96px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(newText, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    material.map = texture;
    material.needsUpdate = true;

    // Update the text in userData
    mesh.userData.text = newText;
  }

  /**
   * Create a cube mesh with specified dimensions
   */
  public createCubeMesh(
    position: THREE.Vector3,
    size = { width: 0.2, height: 0.2, depth: 0.2 }
  ): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(size.width, size.height, size.depth);
    const color = '#ff0000';
    const material = new THREE.MeshBasicMaterial({
      color: color,
    });
    const cube = new THREE.Mesh(geometry, material);

    // Position the cube
    cube.position.copy(position);
    cube.position.y = size.height / 2; // Elevate it above the floor

    // Add metadata for interaction
    cube.userData = {
      isInteractive: true,
      draggable: true,
      isCube: true,
      width: size.width,
      height: size.height,
      depth: size.depth,
      originalColor: color,
    };

    this._addCenterVisualization(cube);
    return cube;
  }

  /**
   * Create a sphere mesh with specified radius
   */
  public createSphereMesh(position: THREE.Vector3, radius = 0.1): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(radius, 32, 32);
    const color = '#00ff00';
    const material = new THREE.MeshBasicMaterial({
      color: color,
    });
    const sphere = new THREE.Mesh(geometry, material);

    // Position the sphere
    sphere.position.copy(position);
    sphere.position.y = radius; // Elevate it above the floor

    // Add metadata for interaction
    sphere.userData = {
      isInteractive: true,
      draggable: true,
      isSphere: true,
      radius: radius,
      originalColor: color,
    };

    this._addCenterVisualization(sphere);
    return sphere;
  }

  /**
   * Create a fixture mesh from a FixtureItem
   */
  public async createFixtureMesh(
    item: FixtureItem,
    position: THREE.Vector3,
    renderType: 'rendered' | 'lined' = 'rendered'
  ): Promise<THREE.Mesh> {
    let renderPath = '';

    if (item.figure?.renders) {
      if (renderType === 'rendered') {
        renderPath = item.figure.renders.topdown_l || '';
      } else {
        renderPath = item.figure.renders.front || '';
      }
    }

    // Create placeholder mesh that will hold the 3D model
    const geometry = new THREE.PlaneGeometry(item.size.width, item.size.height);
    const material = new THREE.MeshBasicMaterial({
      color: item.color || 0xcccccc,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
    });

    // Create mesh with placeholder material
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = -Math.PI / 2; // Rotate to lie flat on the floor
    mesh.position.copy(position);
    mesh.position.y = 0.02; // Slightly above the floor

    // Store fixture data
    mesh.userData = {
      isInteractive: true,
      fixtureId: item.id,
      type: 'fixture',
      name: item.name,
      category: item.category,
      size: item.size,
      renderType,
      is3DModel: true,
      imageUrl: item.imageUrl,
    };

    // Load the 3D model if render path is available
    if (renderPath) {
      try {
        const model = await this.loadGLBModel(renderPath);
        this.setupFixtureModel(mesh, model, item, renderPath);
      } catch (error) {
        console.error(`Error loading 3D model from ${renderPath}:`, error);
      }
    }

    this._addCenterVisualization(mesh);
    return mesh;
  }

  /**
   * Update mesh geometry and userData
   */
  public updateMeshGeometry(mesh: THREE.Mesh, updates: any): void {
    // Dispose of the old geometry to prevent memory leaks
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }

    // Update the mesh geometry and userData
    mesh.geometry = updates.geometry;
    mesh.geometry.computeVertexNormals();
    mesh.geometry.attributes.position.needsUpdate = true;
    mesh.geometry.attributes.normal.needsUpdate = true;
    mesh.geometry.attributes.uv.needsUpdate = true;
    mesh.userData = updates.userData;
  }

  /**
   * Update mesh color
   */
  public updateMeshColor(mesh: THREE.Mesh, color: string): void {
    // Update the material color
    const material = mesh.material as THREE.MeshBasicMaterial;
    material.color.setStyle(color);
    material.needsUpdate = true;

    // Update the userData
    mesh.userData.originalColor = color;
  }

  /**
   * Dispose of mesh resources
   */
  public disposeMesh(mesh: THREE.Mesh): void {
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }

    if (mesh.material) {
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((mat) => mat.dispose());
      } else {
        mesh.material.dispose();
      }
    }

    // Dispose of children recursively
    mesh.children.forEach((child) => {
      if (child instanceof THREE.Mesh) {
        this.disposeMesh(child);
      }
    });
  }

  /**
   * Load GLB model with caching
   */
  private loadGLBModel(url: string): Promise<THREE.Group> {
    return new Promise((resolve, reject) => {
      if (this.modelCache[url]) {
        resolve(this.modelCache[url].clone());
        return;
      }

      this.gltfLoader.load(
        url,
        (gltf) => {
          this.modelCache[url] = gltf.scene;
          resolve(gltf.scene.clone());
        },
        undefined,
        (error) => {
          console.error('Error loading GLB model:', error);
          reject(error);
        }
      );
    });
  }

  /**
   * Setup fixture model after loading
   */
  private setupFixtureModel(
    mesh: THREE.Mesh,
    model: THREE.Group,
    item: FixtureItem,
    renderPath: string
  ): void {
    console.log(`Successfully loaded 3D model: ${renderPath}`, model);

    // Remove the placeholder plane
    while (mesh.children.length > 0) {
      mesh.remove(mesh.children[0]);
    }

    if (renderPath.includes('walmart-texture.glb')) {
      // Make sure textures are preserved for Walmart models
      model.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const meshChild = child as THREE.Mesh;
          if (meshChild.material) {
            this.setupMaterialProperties(meshChild.material);
          }
        }
      });
    }

    // Scale model to fit within the intended size
    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3();
    box.getSize(size);

    const scaleX = item.size.width / size.x;
    const scaleZ = item.size.height / size.z;
    const scale = Math.min(scaleX, scaleZ);

    model.scale.set(scale, scale, scale);

    // Rotate model to face correct direction
    model.rotation.x = Math.PI / 2;
    mesh.add(model);
  }

  /**
   * Setup material properties for better rendering
   */
  private setupMaterialProperties(
    material: THREE.Material | THREE.Material[]
  ): void {
    const materials = Array.isArray(material) ? material : [material];

    materials.forEach((mat) => {
      const standardMat = mat as
        | THREE.MeshStandardMaterial
        | THREE.MeshBasicMaterial;

      if (standardMat.map) {
        standardMat.needsUpdate = true;
        standardMat.transparent = true;
        standardMat.side = THREE.DoubleSide;

        // Additional properties for better texture rendering
        if (standardMat instanceof THREE.MeshStandardMaterial) {
          standardMat.roughness = 0.5;
          standardMat.metalness = 0.1;
          standardMat.envMapIntensity = 1.0;
        }

        // Ensure texture is properly applied
        if (standardMat.map) {
          standardMat.map.needsUpdate = true;
        }
      }
    });
  }

  /**
   * Clear model cache
   */
  public clearCache(): void {
    this.modelCache = {};
  }

  /**
   * Get cached model count
   */
  public getCacheSize(): number {
    return Object.keys(this.modelCache).length;
  }

  /**
   * Clone a mesh with directional positioning
   */
  public cloneDirectionalMesh(
    mesh: THREE.Mesh,
    direction: 'left' | 'right'
  ): THREE.Mesh {
    const clonedMesh = mesh.clone();

    // Calculate offset direction based on mesh's current rotation
    const directionVector = new THREE.Vector3(
      direction === 'left' ? -1 : 1,
      0,
      0
    );
    directionVector.applyEuler(mesh.rotation);

    // Calculate offset distance using the mesh's width
    const offsetDistance = mesh.userData.size.width;
    const offset = directionVector.multiplyScalar(offsetDistance);

    // Apply the offset to position
    clonedMesh.position.copy(mesh.position).add(offset);

    // Remove any existing children first
    while (clonedMesh.children.length > 0) {
      clonedMesh.remove(clonedMesh.children[0]);
    }

    // Clone all children (including the loaded model)
    mesh.children.forEach((child) => {
      const clonedChild = child.clone();

      // Handle userData for child meshes
      if (clonedChild instanceof THREE.Mesh) {
        if (child.userData.isCenterPointVisualization) {
          // For center visualization sprites
          clonedChild.userData = {
            isCenterPointVisualization: true,
          };
        } else {
          // For model parts
          clonedChild.userData = {
            isInteractive: false,
            parentContainer: clonedMesh,
          };
        }
      }

      clonedMesh.add(clonedChild);
    });

    // Ensure the cloned mesh has its own unique identifiers
    clonedMesh.uuid = THREE.MathUtils.generateUUID();

    // Update the name and ensure all parent mesh properties are preserved
    const direction_suffix = direction === 'left' ? 'Left' : 'Right';
    clonedMesh.userData = {
      ...mesh.userData,
      name: `${mesh.userData.name} ${direction_suffix}`,
      isInteractive: true,
      draggable: true,
      is3DModel: true,
    };

    // Add center visualization to the clone
    this._addCenterVisualization(clonedMesh);

    return clonedMesh;
  }

  public cloneMesh(mesh: THREE.Mesh, name: string): THREE.Mesh {
    const clonedMesh = mesh.clone();
    clonedMesh.userData.name = name;
    return clonedMesh;
  }

  private _addCenterVisualization(object: THREE.Mesh): void {
    // Ensure we have up-to-date world matrices so boundingBox is correct
    object.updateWorldMatrix(true, false);

    const boundingBox = new THREE.Box3().setFromObject(object);
    const centerWorld = new THREE.Vector3();
    boundingBox.getCenter(centerWorld);

    // Create a sprite so it always faces the camera and ignores depth testing
    const spriteMaterial = new THREE.SpriteMaterial({
      color: 0xff0000,
      depthTest: false, // Always render on top of the object itself
      depthWrite: false,
    });

    const redDot = new THREE.Sprite(spriteMaterial);
    redDot.scale.set(0.1, 0.1, 0.1); // Size of the red dot (adjust as needed)

    // Position the sprite at the center of the object
    const offsetWorld = centerWorld.clone();

    // Convert world position to the object's local space so the sprite moves with the mesh
    object.worldToLocal(offsetWorld);
    redDot.position.copy(offsetWorld);

    redDot.renderOrder = 999; // Ensure it renders last
    redDot.userData = { isCenterPointVisualization: true };

    object.add(redDot);
  }
}

// Export singleton instance
export const meshFactory = new MeshFactory();
